import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSections } from "../../hooks/use-sections";
import { useCourses } from "../../hooks/use-courses";
import Button from "../../components/button";
import CourseItem from "../../components/course-item";
import PageContainer from "../../components/page-container";
import s from "./styles.module.scss";
import PageHeader from "../../components/page-header";

const DEFAULT_COLORS = [
  '#d50000', // Tomato
  '#e67c73', // Flamingo  
  '#f4511e', // Tangerine
  '#f6bf26', // Banana
  '#33b679', // Sage
  '#0b8043', // Basil
  '#039be5', // Peacock (default)
  '#3f51b5', // Blueberry
  '#7986cb', // Lavender
  '#8e24aa', // Grape
  '#616161', // Graphite
];

const SelectSections = () => {
  const navigate = useNavigate();
  const { sections, updateSections } = useSections();
  const { courses } = useCourses();
  const [selectedSections, setSelectedSections] = useState({});
  const [dropdownStates, setDropdownStates] = useState({});
  const [allSections, setAllSections] = useState({});
  const [selectedColors, setSelectedColors] = useState({}); 


  // Function to merge backend sections with user-created sections
  const mergeAllSections = useCallback(() => {
    const merged = { ...sections };
  
    // Get user-created course data from localStorage or sessionStorage (OAuth fallback)
    let savedCourseData = localStorage.getItem("userCourseData");
    if (!savedCourseData) {
      savedCourseData = sessionStorage.getItem("userCourseData");
    }
  
    let userCourseData = {};
  
    if (savedCourseData) {
      try {
        userCourseData = JSON.parse(savedCourseData);
      } catch (error) {
        console.error("Error parsing user course data:", error);
      }
    }
  
    // Also get the allSectionsData that was saved from choose-sections
    let savedAllSections = localStorage.getItem("allSectionsData");
    if (!savedAllSections) {
      savedAllSections = sessionStorage.getItem("allSectionsData");
    }
  
    if (savedAllSections) {
      try {
        const parsedAllSections = JSON.parse(savedAllSections);
        // Merge saved sections with existing sections
        Object.entries(parsedAllSections).forEach(([key, value]) => {
          // Only add if not already in merged (to avoid duplicates)
          if (!merged[key] || (merged[key].sections?.length === 0 && value.sections?.length > 0)) {
            merged[key] = value;
          }
        });
      } catch (error) {
        console.error("Error parsing all sections data:", error);
      }
    }
    // For each course, handle user-created sections (both sync and async)
    Object.entries(userCourseData).forEach(([courseKey, courseInfo]) => {
      // Handle asynchronous courses
      if (courseInfo.isAsynchronous) {
        // Create or update the course entry for async
        merged[courseKey] = {
          course_name: courseInfo.courseName || courseKey.replace("uploads/", ""),
          pdf: courseKey,
          sections: [{
            name: "Asynchronous",
            timing: null,
            isAsynchronous: true,
            isUserCreated: true
          }],
          color: courseInfo.color,
          location: "Asynchronous"
        };
      } 
      // Handle synchronous courses with times (manual section creation)
      else if (courseInfo.courseTime || courseInfo.courseTimes) {
        // Create course entry if it doesn't exist (for manual-only courses)
        if (!merged[courseKey]) {
          merged[courseKey] = {
            course_name: courseInfo.courseName || courseKey.replace("uploads/", ""),
            pdf: courseKey,
            sections: [],
            color: courseInfo.color,
            location: courseInfo.location || "TBD",
          };
        }
        
        let userSections;
        
        // New format: single time range for all days (ONE section)
        if (courseInfo.courseTime && courseInfo.selectedDays) {
          const daysString = courseInfo.selectedDays.map(day => 
            day.toUpperCase())
            .join("/");
          userSections = [{
            name: `${courseInfo.courseName}`,
            days_of_week: daysString,  // Array of days for this single section
            timing: `${courseInfo.courseTime.from} - ${courseInfo.courseTime.to}`,
            isUserCreated: true,
          }];
        }
        // Old format (backwards compatibility if needed)
        else if (courseInfo.courseTimes) {
          userSections = Object.entries(courseInfo.courseTimes).map(
            ([day, times]) => ({
              name: `${courseInfo.courseName} - ${day}`,
              timing: `${times.from} - ${times.to}`,
              day: day,
              isUserCreated: true,
            })
          );
        }
  
        // For courses that originally had no sections, replace with user sections
        if (!merged[courseKey].sections || merged[courseKey].sections.length === 0) {
          merged[courseKey].sections = userSections;
        } else {
          // Course had backend sections, combine with user sections (avoid duplicates)
          const existingUserSections = merged[courseKey].sections.filter(s => s.isUserCreated);
          const backendSections = merged[courseKey].sections.filter(s => !s.isUserCreated);
  
          // Only add user sections if they're not already there
          if (existingUserSections.length === 0) {
            merged[courseKey].sections = [...backendSections, ...userSections];
          }
        }
  
        // Update course name if user provided one
        if (courseInfo.courseName) {
          merged[courseKey].course_name = courseInfo.courseName;
        }

        if (courseInfo.color) {
          merged[courseKey].color = courseInfo.color;
        }
      }
    });
  
    console.log("Merged sections result:", merged);
    return merged;
  }, [sections]);

  useEffect(() => {
    if (Object.keys(sections).length === 0) {
      navigate("/upload");
      return;
    }
  
    // Merge all sections (backend + user-created)
    const mergedSections = mergeAllSections();
    setAllSections(mergedSections);
  
    // Check if we have saved selected sections from choose-sections
    let savedSelectedSections = localStorage.getItem("selectedSections");
    if (!savedSelectedSections) {
      savedSelectedSections = sessionStorage.getItem("selectedSections");
    }
  
    if (savedSelectedSections) {
      try {
        const parsed = JSON.parse(savedSelectedSections);
        setSelectedSections(parsed);
      } catch (error) {
        console.error("Error parsing saved selected sections:", error);
        // Fall back to defaults
        const defaults = {};
        Object.entries(mergedSections).forEach(([courseKey, courseData]) => {
          if (courseData.sections && courseData.sections.length > 0) {
            defaults[courseKey] = courseData.sections[0];
          }
        });
        setSelectedSections(defaults);
      }
    } else {
      // Initialize with first section of each course that has sections
      const defaults = {};
      Object.entries(mergedSections).forEach(([courseKey, courseData]) => {
        if (courseData.sections && courseData.sections.length > 0) {
          defaults[courseKey] = courseData.sections[0];
        }
      });
      setSelectedSections(defaults);
    }
    // Load saved colors or assign default colors
    let savedColors = localStorage.getItem("selectedColors");
    if (!savedColors) {
      savedColors = sessionStorage.getItem("selectedColors");
    }
    
    if (savedColors) {
      try {
        const parsedColors = JSON.parse(savedColors);
        setSelectedColors(parsedColors);
      } catch (error) {
        console.error("Error parsing saved colors:", error);
      }
    } else {
      // Assign default colors to courses
      const defaultColors = {};
      let colorIndex = 0;
      Object.entries(mergedSections).forEach(([courseKey, courseData]) => {
        if (courseData.sections && courseData.sections.length > 0) {
          // Use saved color from course data if available, otherwise assign from palette
          defaultColors[courseKey] = courseData.color || DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length];
          colorIndex++;
        }
      });
      setSelectedColors(defaultColors);
    }
  }, [sections, navigate, mergeAllSections]);

  const handleSectionChange = (courseKey, section) => {
    setSelectedSections((prev) => ({
      ...prev,
      [courseKey]: section,
    }));

    // Close dropdown after selection
    setDropdownStates((prev) => ({
      ...prev,
      [courseKey]: false,
    }));
  };

  // ADD COLOR CHANGE HANDLER
  const handleColorChange = (courseKey, color) => {
    setSelectedColors((prev) => ({
      ...prev,
      [courseKey]: color,
    }));
    
    // Update the color in allSections as well
    setAllSections((prev) => ({
      ...prev,
      [courseKey]: { ...prev[courseKey], color: color },
    }));
  };

  const handleConfirmSections = () => {
    // Only keep sections that are currently displayed (coursesWithSections)
    const displayedCourseKeys = coursesWithSections.map(([courseKey]) => courseKey);
    
    // Filter selectedSections to only include displayed courses
    const cleanedSelectedSections = {};
    displayedCourseKeys.forEach(courseKey => {
      if (selectedSections[courseKey]) {
        cleanedSelectedSections[courseKey] = selectedSections[courseKey];
      }
    });

     // Filter selectedColors to only include displayed courses
    const cleanedSelectedColors = {};
    displayedCourseKeys.forEach(courseKey => {
      if (selectedColors[courseKey]) {
        cleanedSelectedColors[courseKey] = selectedColors[courseKey];
      }
    });
    
    // Filter allSections to only include displayed courses
    const cleanedAllSections = {};
    displayedCourseKeys.forEach(courseKey => {
      if (allSections[courseKey]) {
        cleanedAllSections[courseKey] = {
          ...allSections[courseKey],
          color: cleanedSelectedColors[courseKey] || '#039be5' // Default to Peacock if missing 
        };
      }
    });


    
    // Store only the cleaned versions
    localStorage.setItem("selectedSections", JSON.stringify(cleanedSelectedSections));
    sessionStorage.setItem("selectedSections", JSON.stringify(cleanedSelectedSections));
    
    console.log("[DEBUG] Cleaned selected sections stored:", cleanedSelectedSections);
    
    localStorage.setItem("allSectionsData", JSON.stringify(cleanedAllSections));
    sessionStorage.setItem("allSectionsData", JSON.stringify(cleanedAllSections));
    
    console.log("[DEBUG] Cleaned all sections data stored:", cleanedAllSections);
  
    // Rest of your existing navigation logic...
    const coursesWithoutSections = Object.entries(cleanedAllSections).filter(
      ([_, courseData]) =>
        !courseData.sections || courseData.sections.length === 0
    );
  
    if (coursesWithoutSections.length > 0) {
      navigate("/choose-sections");
    } else {
      navigate("/select-calendar");
    }
  };

  // Filter courses that have sections to display
  const coursesWithSections = Object.entries(allSections).filter(
    ([courseKey, courseData]) => {
      const hasSections = courseData.sections && courseData.sections.length > 0;
      return hasSections;
    }
  );

  // Add effect to handle case when no courses have sections
  useEffect(() => {
    if (
      Object.keys(allSections).length > 0 &&
      coursesWithSections.length === 0
    ) {
      // No courses with sections, go directly to calendar selection
      navigate("/select-calendar");
    }
  }, [allSections, coursesWithSections.length, navigate]);

  if (coursesWithSections.length === 0) {
    return null;
  }

  const handleCourseNameChange = (courseKey, newName) => {
    setAllSections((prev) => ({
      ...prev,
      [courseKey]: { ...prev[courseKey], course_name: newName },
    }));
  };

  const renameCourse = (courseKey) => {};

  return (
    <PageContainer>
      <div className={s.container}>
        <PageHeader
          title="Customize Course Sections"
          description="Choose a time that works best for each course"
        />

        <div className={`${s.coursesList} ${coursesWithSections.length === 1 ? s.singleCourse : ''}`}>
          {coursesWithSections.map(([courseKey, courseData]) => (
            <CourseItem
              key={courseKey}
              courseData={courseData}
              selectedSections={selectedSections}
              selectedColor={selectedColors[courseKey]}
              courseKey={courseKey}
              onSectionChange={handleSectionChange}
              onCourseNameChange={handleCourseNameChange}
              onColorChange={handleColorChange}
            />
          ))}
        </div>

        <div className={s.confirmActions}>
          <Button
            variant="primary"
            size="large"
            onClick={handleConfirmSections}
            className={s.confirmButton}
          >
            Confirm sections
          </Button>
        </div>
      </div>
    </PageContainer>
  );
};

export default SelectSections;
